// ============================================================================
// DynamicStateSection — case-detail right pane for the currently-selected STAGE
// ============================================================================
// Given a stage (a grouping of one or more workflow states), renders:
//   • A horizontal tab strip — one tab per inner state, with status marker
//   • The DynamicForm for the active tab's state
//
// Active-tab selection rule:
//   1. If the workflow's current state is in this stage, default to it
//   2. Else default to the LAST done state in this stage (most recent work)
//   3. Else default to the first state (future stages)
//
// The form's read-only / edit-able state is derived from the active tab's
// status — past/closed states render read-only, current allows save, future
// allows save (to pre-fill before reaching).

import React, { useCallback, useMemo, useState, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useLatestCaseStateData, useSaveCaseStateData } from 'hooks/api/useCaseStateData';
import { useWorkflowChecklist, type ChecklistStage, type ChecklistStep } from 'recsuit-workflow';
import { DynamicForm } from 'components/common/DynamicForm';
import type { FormSpec, FormValues } from 'api/types/formSpec.types';
import { materializeFormSpec } from 'components/common/DynamicForm/noticeWidget';
import type { LitigationCase } from 'api/types/litigationCase.types';
import CaseSecuritiesPanel from './CaseSecuritiesPanel';
import NoticeGenerateModal from '../components/NoticeGenerateModal';

// State codes where the in-scope security list should be shown as a
// "Targets" strip above the form (repossession-action states).
const STATES_SHOWING_TARGETS = new Set([
  'REPO_ASSIGNED', 'REPO_IN_PROGRESS', 'REPOSSESSED',
]);

// Map state_code → notice template code. Adding entries here exposes a
// "Generate Notice" button at the top of those state panels.
const STATE_NOTICE_TEMPLATE: Record<string, string> = {
  PRE_REPO_NOTICE_ISSUED: 'ESAF-SMA-1-2',
  // NOTE: SARFAESI notice stages now carry their own "Generate Notice" button
  // via the reusable Notice widget (form_spec section type: 'notice'), so they
  // are intentionally NOT listed here — that would double the button.
};

export interface DynamicStateSectionProps {
  kase: LitigationCase;
  stageCode: string;
  onSaved?: () => void;
}

const DynamicStateSection: React.FC<DynamicStateSectionProps> = ({ kase, stageCode, onSaved }) => {
  const { data: checklist, loading: checklistLoading } = useWorkflowChecklist(kase.workflowInstanceId ?? null);

  const stage = useMemo<ChecklistStage | null>(() => {
    if (!checklist) return null;
    return checklist.stages.find((s) => s.stageCode === stageCode) ?? null;
  }, [checklist, stageCode]);

  // Active inner state — defaults to current, else last done, else first.
  const [activeStateCode, setActiveStateCode] = useState<string | null>(null);

  useEffect(() => {
    if (!stage) return;
    if (activeStateCode && stage.steps.some((s) => s.state.stateCode === activeStateCode)) return;
    const current = stage.steps.find((s) => s.status === 'current');
    if (current) { setActiveStateCode(current.state.stateCode); return; }
    const doneInOrder = stage.steps.filter((s) => s.status === 'done');
    if (doneInOrder.length) { setActiveStateCode(doneInOrder[doneInOrder.length - 1].state.stateCode); return; }
    setActiveStateCode(stage.steps[0]?.state.stateCode ?? null);
  }, [stage, activeStateCode]);

  const activeStep = useMemo<ChecklistStep | null>(() => {
    if (!stage || !activeStateCode) return null;
    return stage.steps.find((s) => s.state.stateCode === activeStateCode) ?? null;
  }, [stage, activeStateCode]);

  if (checklistLoading || !checklist) {
    return <SectionShell title={stage?.stageLabel ?? stageCode}>
      <div style={loadingStyle}>Loading…</div>
    </SectionShell>;
  }

  if (!stage) {
    return <SectionShell title={stageCode}>
      <div style={loadingStyle}>Stage not found in this workflow.</div>
    </SectionShell>;
  }

  return (
    <SectionShell title={stage.stageLabel} icon={stage.stageIcon} stage={stage}>
      {stage.steps.length > 1 && (
        <TabStrip
          steps={stage.steps}
          activeStateCode={activeStateCode}
          onSelect={setActiveStateCode}
        />
      )}
      {activeStep && (
        <StateFormPanel
          kase={kase}
          step={activeStep}
          onSaved={onSaved}
        />
      )}
    </SectionShell>
  );
};

// ----------------------------------------------------------------------------
// Section shell — stage header + body
// ----------------------------------------------------------------------------

const SectionShell: React.FC<{
  title: string;
  icon?: string;
  stage?: ChecklistStage;
  children: React.ReactNode;
}> = ({ title, icon, stage, children }) => {
  const IconComp = icon ? (IconSystem as any)[icon] : null;
  // No badge for future stages — the rail's hollow dot + dimmed label already
  // convey "not yet entered". Stamping "Upcoming" on the stage the user has
  // navigated to (often the very next action) reads as discouraging.
  const badge =
    !stage ? null :
    stage.status === 'current' ? { label: 'Current stage', bg: '#dbeafe', fg: '#1e40af' } :
    stage.status === 'done'    ? { label: `Completed (${stage.doneCount}/${stage.totalCount})`, bg: '#dcfce7', fg: '#166534' } :
                                  null;
  return (
    <div>
      <div style={headerStyle}>
        {IconComp && <IconComp size={20} />}
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>{title}</h3>
        {badge && (
          <span style={{
            marginLeft: 'auto', fontSize: '0.72rem', fontWeight: 600,
            padding: '2px 10px', borderRadius: '999px',
            background: badge.bg, color: badge.fg as string,
          }}>
            {badge.label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Tab strip — horizontal list of inner states
// ----------------------------------------------------------------------------

const TabStrip: React.FC<{
  steps: ChecklistStep[];
  activeStateCode: string | null;
  onSelect: (stateCode: string) => void;
}> = ({ steps, activeStateCode, onSelect }) => (
  <div style={tabStripStyle}>
    {steps.map((step) => {
      const isActive = activeStateCode === step.state.stateCode;
      const dotColor =
        step.status === 'done' ? '#16a34a' :
        step.status === 'current' ? '#2563eb' :
        'var(--text-secondary)';
      return (
        <button
          key={step.state.stateCode}
          type="button"
          onClick={() => onSelect(step.state.stateCode)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.5rem 0.85rem',
            fontSize: '0.82rem',
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            background: isActive ? 'var(--bg-primary)' : 'transparent',
            border: 'none',
            borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: step.status === 'future' ? 'transparent' : dotColor,
            border: step.status === 'future' ? `1.5px solid ${dotColor}` : 'none',
          }} />
          {step.state.displayName}
        </button>
      );
    })}
  </div>
);

// ----------------------------------------------------------------------------
// Single state's form panel — uses DynamicForm internally
// ----------------------------------------------------------------------------

const StateFormPanel: React.FC<{
  kase: LitigationCase;
  step: ChecklistStep;
  onSaved?: () => void;
}> = ({ kase, step, onSaved }) => {
  const { entry, loading, refetch } = useLatestCaseStateData(kase.id, step.state.stateCode);
  const { save } = useSaveCaseStateData();

  const handleSave = useCallback(async (values: FormValues) => {
    await save({
      caseId: kase.id,
      stateCode: step.state.stateCode,
      stateDataId: entry?.id ?? null,
      data: values,
      rowVersion: entry?.rowVersion,
    });
    await refetch();
    onSaved?.();
  }, [save, kase.id, step.state.stateCode, entry?.id, entry?.rowVersion, refetch, onSaved]);

  // ALL hooks must run before any early-return branch below — moving
  // notice-modal state + memo up here keeps the hook order stable across
  // re-renders. (React: "Rendered fewer hooks than expected" otherwise.)
  const [noticeOpen, setNoticeOpen] = React.useState(false);

  // Overrides for the merge — values from the latest persisted state-data
  // (notice_no, notice_date, demand_amount, etc.). Falls through to backend
  // auto-resolved values when keys are missing.
  const noticeOverrides = React.useMemo(() => {
    const d = (entry?.data as Record<string, unknown>) ?? {};
    const out: Record<string, string | null> = {};
    // Ordered lowest→highest priority: later assignments win, so the more
    // specific SARFAESI keys override the generic vehicle-repo ones when both
    // are present. Vehicle-repo forms use notice_no / demand_amount; the
    // SARFAESI notice forms use notice_number and amount fields per stage.
    const fields: Array<{ from: string; to?: string }> = [
      { from: 'notice_no' },
      { from: 'notice_number', to: 'notice_no' },        // SARFAESI notices
      { from: 'notice_date' },
      { from: 'cooling_off_days' },
      // Amounts → the two overdue placeholders used by every template
      { from: 'demand_amount', to: 'overdue_amount' },
      { from: 'demand_amount', to: 'total_overdue' },
      { from: 'overdue_amount', to: 'overdue_amount' },  // SARFAESI Regularisation
      { from: 'outstanding_amount', to: 'overdue_amount' },
      { from: 'outstanding_amount', to: 'total_overdue' }, // SARFAESI Reg / LRN
      { from: 'amount_demanded', to: 'overdue_amount' },   // SARFAESI §13(2)
      { from: 'amount_demanded', to: 'total_overdue' },
    ];
    for (const f of fields) {
      const v = d[f.from];
      if (v != null && v !== '') out[f.to ?? f.from] = String(v);
    }
    return out;
  }, [entry?.data]);

  if (loading) {
    return <div style={loadingStyle}>Loading entry…</div>;
  }

  // Expand any reusable widget sections (notice, …) into real fields so
  // validation + rendering share one materialised spec.
  const spec = materializeFormSpec((step.state.formSpec as FormSpec | undefined) ?? null) ?? undefined;
  if (!spec) {
    return (
      <div style={infoPanelStyle}>
        <IconSystem.info size={24} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
        <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>No data capture for this step</div>
        <div style={{ fontSize: '0.78rem' }}>Advance via the action bar to fire the next transition.</div>
      </div>
    );
  }

  const showTargets = STATES_SHOWING_TARGETS.has(step.state.stateCode);
  const noticeTemplateCode = STATE_NOTICE_TEMPLATE[step.state.stateCode];

  return (
    <>
      {showTargets && (
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.5rem 0.65rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: 4,
        }}>
          <div style={{
            fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
            letterSpacing: '0.04em', color: 'var(--text-secondary)',
            marginBottom: '0.35rem',
          }}>Targets</div>
          <CaseSecuritiesPanel caseId={kase.id} compact />
        </div>
      )}

      {noticeTemplateCode && (
        <div style={{
          marginBottom: '0.75rem',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setNoticeOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
          >
            <IconSystem.mail size={14} />
            Generate Notice
          </button>
        </div>
      )}

      <DynamicForm
        spec={spec}
        initial={(entry?.data as FormValues) ?? {}}
        readOnly={false}
        onSave={handleSave}
        confirmOnSave={true}
        saveLabel={entry ? 'Update' : 'Save'}
        seedContext={{ caseId: kase.id, caseCode: kase.caseCode, kase }}
      />

      {noticeTemplateCode && (
        <NoticeGenerateModal
          isOpen={noticeOpen}
          caseId={kase.id}
          caseCode={kase.caseCode}
          templateCode={noticeTemplateCode}
          overrides={noticeOverrides}
          onClose={() => setNoticeOpen(false)}
        />
      )}
    </>
  );
};

// ----------------------------------------------------------------------------

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.5rem 0',
  marginBottom: '0.5rem',
  borderBottom: '1px solid var(--border-color)',
};

const tabStripStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap',
  gap: '0.25rem',
  borderBottom: '1px solid var(--border-color)',
  marginBottom: '0.85rem',
};

const loadingStyle: React.CSSProperties = {
  padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem',
};

const infoPanelStyle: React.CSSProperties = {
  padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)',
};

export default DynamicStateSection;
